import React, { useEffect, useRef } from 'react';
import { useGlobalEvents } from './global-events';
import {
  EventBrokerCallback,
  EventBrokerCallbackRecord,
  EventBrokerContext,
} from './event-broker-context';
import { ProjectInteraction, VevDispatchEvent, VevTriggerType } from '@vev/utils';
import { useGlobalStore } from '../../core/';

interface Props {
  children: React.ReactElement;
}

// █▀▄▀█ █▀▀ █▀ █▀ ▄▀█ █▀▀ █▀▀   █▄▄ █▀█ █▀█ █▄▀ █▀▀ █▀█
// █░▀░█ ██▄ ▄█ ▄█ █▀█ █▄█ ██▄   █▄█ █▀▄ █▄█ █░█ ██▄ █▀▄
//
//                     ▄░▀▀▀▀░▄
//              ██    █░██▀██░█▄    YO!
//               █      ▀▄ ▀ ▄▀
//               █▄▄▄▄   ████

export function EventBrokerProvider({ children }: Props) {
  useGlobalEvents();
  const eventCallbacks = useRef<EventBrokerCallbackRecord>({});

  const projectInteractions = useGlobalStore((store) => {
    const { interactions } = store;
    return interactions;
  });

  useEffect(() => {
    function handleEvent(event: CustomEvent<VevDispatchEvent>) {
      const isTrigger = (interaction: ProjectInteraction) =>
        event.detail.type === interaction.trigger?.type &&
        event.detail.contentKey === interaction.trigger?.contentKey;

      const isEvent = (interaction: ProjectInteraction) =>
        event.detail.type === interaction.event?.type &&
        event.detail.contentKey === interaction.event?.contentKey;

      const interactions = projectInteractions.filter((interaction) => {
        return isEvent(interaction) || isTrigger(interaction);
      });

      interactions.forEach((interaction) => {
        const callbackName = `${interaction?.event?.type}.${interaction?.event?.contentKey}`;
        const callback = eventCallbacks.current[callbackName];
        if (callback) {
          callback((interaction.event?.args || event.detail.args) as any);
        }
      });
    }

    window.addEventListener('@@vev', handleEvent as EventListener);

    return () => {
      window.removeEventListener('@@vev', handleEvent as EventListener);
    };
  }, [projectInteractions]);

  function addCallback(
    event: VevTriggerType,
    contentKey: string | undefined,
    callback: EventBrokerCallback,
  ) {
    if (event && contentKey) {
      eventCallbacks.current[`${event}.${contentKey}`] = callback;
    }
  }

  function removeCallback(event: VevTriggerType, contentKey: string | undefined) {
    if (event && contentKey) {
      delete eventCallbacks.current[`${event}.${contentKey}`];
    }
  }

  return (
    <EventBrokerContext.Provider value={{ addCallback, removeCallback }}>
      {children}
    </EventBrokerContext.Provider>
  );
}
